import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { StitchedPMark } from '../components/StitchedPMark';
import { Patch } from '../components/Patch';
import { supabase } from '../lib/supabase';

type LandingTask = { text: string; icon: string | null; points: number };
type LandingPatchLive = {
  slug: string;
  name: string;
  tagline: string | null;
  art_url: string | null;
  palette: string;
  art_subject: string | null;
  goal_points: number;
  tasks: LandingTask[];
};

// Featured patches — use the real generated art from Troop 1 so the landing
// page shows what the app actually produces, not mockups. Tasks are fetched
// live so editing a patch in-app flows through to the marketing page; art
// also prefers the live URL so a regenerated patch updates here for free.
const FEATURED_PATCHES: Array<{
  slug: string;
  subject: string;
  palette: string;
  name: string;
  tagline: string;
  artUrl: string | null;
  creator: string;
}> = [
  {
    slug: 'good-neighbor',
    subject: 'generic',
    palette: 'forest',
    name: 'GOOD NEIGHBOR',
    tagline: 'A heart that serves. A neighborhood that thrives.',
    artUrl:
      'https://iknjaqzbdycneqvckvif.supabase.co/storage/v1/object/public/patches-art/d907c967-7dcb-4f31-bf04-fd4033f092a3/1777289649840.png',
    creator: 'Ryan',
  },
  {
    slug: 'daily-bread',
    subject: 'fit_for_life',
    palette: 'gold',
    name: 'DAILY BREAD',
    tagline: 'What you eat shapes who you are.',
    artUrl:
      'https://iknjaqzbdycneqvckvif.supabase.co/storage/v1/object/public/patches-art/8ddfc7ff-ed5b-45c9-82a9-ba6899d49c94/1777289618580.png',
    creator: 'Ryan',
  },
  {
    slug: 'cover-to-cover',
    subject: 'reading',
    palette: 'navy',
    name: 'COVER TO COVER',
    tagline: 'From Genesis to Revelation.',
    artUrl: null,
    creator: 'Ryan',
  },
];

// Color tokens for the dark forest canvas. Body type goes light here.
const ON_DARK = 'var(--cream)';
const ON_DARK_SOFT = 'rgba(251,245,228,0.78)';

export function Landing() {
  return (
    <div
      className="canvas-bg"
      style={{
        position: 'fixed',
        inset: 0,
        overflow: 'auto',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <div
        style={{
          maxWidth: 960,
          margin: '0 auto',
          padding: '64px 24px 96px',
          display: 'flex',
          flexDirection: 'column',
          gap: 96,
        }}
      >
        <Hero />
        <WhatIsAPatch />
        <HowItWorks />
        <WhyDifferent />
        <FinalCta />
        <Footer />
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: 20,
        paddingTop: 32,
      }}
    >
      <StitchedPMark size={96} />
      <h1
        style={{
          fontFamily: 'Bevan',
          fontSize: 'clamp(40px, 8vw, 72px)',
          color: ON_DARK,
          letterSpacing: '0.02em',
          lineHeight: 1,
          margin: 0,
          marginTop: 8,
          textShadow: '0 1px 0 rgba(0,0,0,0.4)',
        }}
      >
        PATCH LEGACY
      </h1>
      <div
        style={{
          fontFamily: 'JetBrains Mono',
          fontSize: 12,
          color: 'var(--brick)',
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
        }}
      >
        ★ Family Tradition ★
      </div>
      <p
        style={{
          fontFamily: 'Newsreader, serif',
          fontSize: 'clamp(18px, 3vw, 22px)',
          color: ON_DARK,
          lineHeight: 1.5,
          maxWidth: 560,
          margin: '12px 0 0',
        }}
      >
        A family tradition you build together, one patch at a time.
      </p>
      <div
        style={{
          display: 'flex',
          gap: 12,
          marginTop: 24,
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}
      >
        <CtaPrimary to="/login">Sign In</CtaPrimary>
        <CtaQuiet href="#what">Learn more ↓</CtaQuiet>
      </div>
    </section>
  );
}

function WhatIsAPatch() {
  const slugs = FEATURED_PATCHES.map((p) => p.slug);
  const { data: live } = useQuery<LandingPatchLive[]>({
    queryKey: ['landing-patches', slugs.join(',')],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_landing_patches', { p_slugs: slugs });
      if (error) throw error;
      return (data ?? []) as LandingPatchLive[];
    },
    staleTime: 5 * 60 * 1000,
  });
  const liveBySlug = new Map((live ?? []).map((p) => [p.slug, p]));

  return (
    <section
      id="what"
      style={{
        background: 'var(--cream)',
        border: '1.5px solid var(--ink)',
        boxShadow: '4px 4px 0 var(--ink)',
        padding: '40px 28px',
        display: 'flex',
        flexDirection: 'column',
        gap: 32,
        alignItems: 'center',
      }}
    >
      <SectionHeader label="01 · The Idea" title="What's a patch?" onDark={false} />
      <p
        style={{
          fontFamily: 'Newsreader, serif',
          fontSize: 'clamp(17px, 2.4vw, 19px)',
          color: 'var(--ink)',
          lineHeight: 1.65,
          maxWidth: 620,
          margin: 0,
          textAlign: 'center',
        }}
      >
        Like a Boy Scout merit badge — but designed by your family, for the things <em>your</em>{' '}
        family values. Reading together. Showing up for a neighbor. Trying something hard. The
        small habits and traditions you want to pass down.
      </p>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 18,
          width: '100%',
          marginTop: 8,
        }}
      >
        {FEATURED_PATCHES.map((p) => (
          <FeaturedPatchCard key={p.slug} patch={p} live={liveBySlug.get(p.slug) ?? null} />
        ))}
      </div>

      <div
        style={{
          fontFamily: 'JetBrains Mono',
          fontSize: 10,
          color: 'var(--ink-soft)',
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          textAlign: 'center',
          marginTop: 4,
        }}
      >
        ★ Tap a patch to see what it takes ★
      </div>
    </section>
  );
}

function FeaturedPatchCard({
  patch,
  live,
}: {
  patch: (typeof FEATURED_PATCHES)[number];
  live: LandingPatchLive | null;
}) {
  const [flipped, setFlipped] = useState(false);
  const tasks = live?.tasks ?? [];
  const goalPoints = live?.goal_points ?? null;
  const hasBack = tasks.length > 0;

  return (
    <div style={{ perspective: 1200 }}>
      <button
        type="button"
        onClick={() => hasBack && setFlipped((f) => !f)}
        aria-label={flipped ? `Hide tasks for ${patch.name}` : `Show tasks for ${patch.name}`}
        aria-pressed={flipped}
        disabled={!hasBack}
        style={{
          position: 'relative',
          width: '100%',
          height: 440,
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: hasBack ? 'pointer' : 'default',
          transformStyle: 'preserve-3d',
          transition: 'transform 0.7s cubic-bezier(0.4, 0.2, 0.2, 1)',
          transform: flipped ? 'rotateY(180deg)' : 'none',
        }}
      >
        {/* Front face */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'var(--paper)',
            border: '1.5px solid var(--ink)',
            boxShadow: '3px 3px 0 var(--ink)',
            padding: 18,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 10,
            textAlign: 'center',
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            boxSizing: 'border-box',
          }}
        >
          <Patch
            subject={live?.art_subject ?? patch.subject}
            palette={live?.palette ?? patch.palette}
            topText={patch.name}
            bottomText={patch.tagline}
            size={170}
            imageUrl={live?.art_url ?? patch.artUrl}
          />
          <div
            style={{
              fontFamily: 'Bevan',
              fontSize: 16,
              color: 'var(--ink)',
              letterSpacing: '0.04em',
              marginTop: 6,
            }}
          >
            {patch.name}
          </div>
          <div
            style={{
              fontFamily: 'Newsreader, serif',
              fontStyle: 'italic',
              fontSize: 14,
              color: 'var(--forest, #1E3A2A)',
              lineHeight: 1.4,
            }}
          >
            {patch.tagline}
          </div>
          <div
            style={{
              fontFamily: 'JetBrains Mono',
              fontSize: 9,
              color: 'var(--ink-soft)',
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              marginTop: 'auto',
            }}
          >
            BY {patch.creator.toUpperCase()}
            {hasBack && <span style={{ marginLeft: 8 }}>· Tap to see tasks →</span>}
          </div>
        </div>

        {/* Back face */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'var(--cream)',
            border: '1.5px solid var(--ink)',
            boxShadow: '3px 3px 0 var(--ink)',
            padding: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            textAlign: 'left',
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              fontFamily: 'JetBrains Mono',
              fontSize: 9,
              letterSpacing: '0.18em',
              color: 'var(--brick)',
              textAlign: 'center',
            }}
          >
            ★ {patch.name} ★
          </div>
          <div
            style={{
              fontFamily: 'Bevan',
              fontSize: 14,
              color: 'var(--ink)',
              letterSpacing: '0.04em',
              textAlign: 'center',
            }}
          >
            WHAT IT TAKES
          </div>
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              paddingRight: 4,
              marginTop: 4,
            }}
          >
            {tasks.map((t, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  padding: '8px 10px',
                  background: 'var(--paper)',
                  border: '1px solid var(--ink)',
                }}
              >
                <span style={{ fontSize: 16, lineHeight: 1.2, flexShrink: 0 }}>{t.icon ?? '★'}</span>
                <span
                  style={{
                    flex: 1,
                    fontFamily: 'Newsreader, serif',
                    fontSize: 13,
                    color: 'var(--ink)',
                    lineHeight: 1.35,
                  }}
                >
                  {t.text}
                </span>
                <span
                  style={{
                    flexShrink: 0,
                    fontFamily: 'JetBrains Mono',
                    fontSize: 9,
                    letterSpacing: '0.14em',
                    color: 'var(--brick)',
                    padding: '2px 5px',
                    border: '1px solid var(--brick)',
                    background: 'var(--cream)',
                  }}
                >
                  +{t.points} PTS
                </span>
              </div>
            ))}
          </div>
          {goalPoints !== null && (
            <div
              style={{
                marginTop: 4,
                fontFamily: 'JetBrains Mono',
                fontSize: 9,
                letterSpacing: '0.14em',
                color: 'var(--ink-soft)',
                textTransform: 'uppercase',
                textAlign: 'center',
              }}
            >
              ★ Earn at {goalPoints} pts · Tap to flip back
            </div>
          )}
        </div>
      </button>
    </div>
  );
}

function HowItWorks() {
  const steps = [
    {
      num: '01',
      title: 'Design',
      body:
        'A family member picks a value or skill to celebrate, names the patch, writes the tasks, and generates the art. Takes about five minutes.',
    },
    {
      num: '02',
      title: 'Earn',
      body:
        'Anyone in the family can enroll. Submit proof — a photo, a note, the honor system. Another family member verifies. Repeat until the patch is earned.',
    },
    {
      num: '03',
      title: 'Treasure',
      body:
        'Earned patches go onto your personal Sash. Every patch keeps a journal of every proof and ceremony — a thread of memories you can read back in twenty years.',
    },
  ];
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
      <div style={{ alignSelf: 'center' }}>
        <SectionHeader label="02 · The Loop" title="How it works" onDark={true} />
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 20,
        }}
      >
        {steps.map((s) => (
          <div
            key={s.num}
            style={{
              background: 'var(--cream)',
              border: '1.5px solid var(--ink)',
              boxShadow: '4px 4px 0 var(--ink)',
              padding: '24px 22px',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            <div
              style={{
                fontFamily: 'JetBrains Mono',
                fontSize: 11,
                color: 'var(--brick)',
                letterSpacing: '0.18em',
              }}
            >
              {s.num}
            </div>
            <div
              style={{
                fontFamily: 'Bevan',
                fontSize: 22,
                color: 'var(--ink)',
                letterSpacing: '0.04em',
              }}
            >
              {s.title.toUpperCase()}
            </div>
            <p
              style={{
                fontFamily: 'Newsreader, serif',
                fontSize: 16,
                color: 'var(--ink)',
                lineHeight: 1.6,
                margin: 0,
              }}
            >
              {s.body}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

function WhyDifferent() {
  const points = [
    { label: 'PRIVATE', body: 'One troop per app. No public profiles. Your invite code is the only way in.' },
    { label: 'NO ADS', body: "We're not selling your data. There's nothing to advertise against — it's just your family in here." },
    { label: 'KEEPSAKE', body: 'Built to be passed down. Photos, ceremony notes, and the journal stay forever.' },
  ];
  return (
    <section
      style={{
        background: 'var(--cream)',
        border: '1.5px solid var(--ink)',
        boxShadow: '4px 4px 0 var(--ink)',
        padding: '40px 32px',
        display: 'flex',
        flexDirection: 'column',
        gap: 28,
      }}
    >
      <div style={{ alignSelf: 'center' }}>
        <SectionHeader label="03 · The Promise" title="Why it's different" onDark={false} />
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 24,
        }}
      >
        {points.map((p) => (
          <div key={p.label} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div
              style={{
                fontFamily: 'JetBrains Mono',
                fontSize: 11,
                color: 'var(--brick)',
                letterSpacing: '0.18em',
              }}
            >
              ★ {p.label}
            </div>
            <p
              style={{
                fontFamily: 'Newsreader, serif',
                fontSize: 16,
                color: 'var(--ink)',
                lineHeight: 1.6,
                margin: 0,
              }}
            >
              {p.body}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 14,
        textAlign: 'center',
      }}
    >
      <h2
        style={{
          fontFamily: 'Bevan',
          fontSize: 'clamp(28px, 5vw, 40px)',
          color: ON_DARK,
          letterSpacing: '0.03em',
          margin: 0,
          textShadow: '0 1px 0 rgba(0,0,0,0.4)',
        }}
      >
        ALREADY HAVE AN INVITE CODE?
      </h2>
      <CtaPrimary to="/login">Sign In</CtaPrimary>
      <p
        style={{
          fontFamily: 'Newsreader, serif',
          fontSize: 16,
          color: ON_DARK_SOFT,
          margin: '20px 0 0',
        }}
      >
        Want to try it with your family?{' '}
        <a
          href="mailto:ryanwcwilliams@gmail.com?subject=Patch%20Legacy"
          style={{ color: 'var(--cream)', textDecoration: 'underline' }}
        >
          Drop me a line.
        </a>
      </p>
    </section>
  );
}

function Footer() {
  return (
    <footer
      style={{
        borderTop: '1px solid rgba(251,245,228,0.2)',
        paddingTop: 24,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 12,
        fontFamily: 'JetBrains Mono',
        fontSize: 11,
        color: ON_DARK_SOFT,
        letterSpacing: '0.12em',
      }}
    >
      <div>★ PATCH LEGACY · A FAMILY HEIRLOOM</div>
      <Link to="/privacy" style={{ color: ON_DARK_SOFT, textDecoration: 'underline' }}>
        Privacy &amp; Terms
      </Link>
    </footer>
  );
}

function SectionHeader({ label, title, onDark }: { label: string; title: string; onDark: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      <div
        style={{
          fontFamily: 'JetBrains Mono',
          fontSize: 11,
          color: 'var(--brick)',
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </div>
      <h2
        style={{
          fontFamily: 'Bevan',
          fontSize: 'clamp(28px, 5vw, 38px)',
          color: onDark ? ON_DARK : 'var(--ink)',
          letterSpacing: '0.03em',
          margin: 0,
          textShadow: onDark ? '0 1px 0 rgba(0,0,0,0.4)' : 'none',
        }}
      >
        {title.toUpperCase()}
      </h2>
    </div>
  );
}

function CtaPrimary({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px 32px',
        background: 'var(--brick)',
        color: 'var(--cream)',
        border: '1.5px solid var(--ink)',
        boxShadow: '3px 3px 0 var(--ink)',
        fontFamily: 'Bevan',
        fontSize: 16,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        textDecoration: 'none',
      }}
    >
      {children}
    </Link>
  );
}

function CtaQuiet({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px 28px',
        background: 'transparent',
        color: ON_DARK,
        border: '1.5px solid var(--cream)',
        fontFamily: 'JetBrains Mono',
        fontSize: 12,
        letterSpacing: '0.16em',
        textTransform: 'uppercase',
        textDecoration: 'none',
      }}
    >
      {children}
    </a>
  );
}
